// useWbLiveBridges — the connected-bridge list backing the multi-bridge
// selector row (Multi-Bridge UI).
//
// GET /wb/live/bridges returns BOTH the numeric weighbridge_id (the join key
// for the live SSE weight_update / camera_frame streams and the camera-preview
// POST) and the enc_id (what ticket weight capture expects as
// weighbridge_enc_id). Polled so `agent_online` reflects live presence.

import { useQuery } from '@tanstack/react-query';

import { apiClient } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import { queryKeys } from '../../api/queryKeys';

export interface LiveBridge {
  /** Raw numeric weighbridge id — matches SSE weighbridge_id + camera-preview. */
  readonly weighbridge_id: number;
  /** Decryptable id for ticket capture (weighbridge_enc_id). */
  readonly enc_id: string;
  readonly name: string;
  readonly code: string;
  /** A pinned agent has a fresh (≤90s) heartbeat. */
  readonly agent_online: boolean;
  /** The agent's wb_camera_config has a non-null anpr_mode (mig 179). */
  readonly anpr_configured: boolean;
}

export function useWbLiveBridges(opts: { enabled?: boolean } = {}) {
  return useQuery<{ bridges: LiveBridge[] }>({
    queryKey: queryKeys.wb.liveBridges,
    queryFn: async () => {
      const res = await apiClient.get<{ bridges: LiveBridge[] }>(endpoints.wb.liveBridges);
      return res.data;
    },
    enabled: opts.enabled ?? true,
    staleTime: 15_000,
    // Keep presence dots fresh while the selector is on screen.
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
}
